import { storageWithExpiration } from "@/helpers/storageHelpers";
import axios, { AxiosError, AxiosResponse, Method } from "axios";
import { readonly, ref } from "vue";

axios.defaults.withCredentials = true;

const devMode = process.env.NODE_ENV !== "production";
const serviceUnavailable = ref(false);
const apiTokenKey = "token";

const storage = storageWithExpiration(localStorage);

const useApi = (displayErrors = true) => {
    const getToken = () => storage.getItem(apiTokenKey);
    const setToken = (value: string) => storage.setItem(apiTokenKey, value);
    const removeToken = () => {
        storage.removeItem(apiTokenKey);
    };

    const setTokenFromResponse = (resp: AxiosResponse) => {
        if (resp?.headers["authorization"]) {
            setToken(resp.headers["authorization"]);
        }
    };

    const post = <T>(url: string, data: unknown, headers: Record<string, string> = {}, signal?: AbortSignal): Promise<AxiosResponse<T>> => {
        return execute<T>(axios.post<T>(url, data, getRequestConfig("POST", data, headers, signal)));
    };

    const get = <T>(url: string, data?: unknown, headers: Record<string, string> = {}, signal?: AbortSignal): Promise<AxiosResponse<T>> => {
        return execute<T>(axios.get<T>(url, getRequestConfig("GET", data, headers, signal)));
    };

    const del = <T>(url: string, data: unknown, headers: Record<string, string> = {}): Promise<AxiosResponse<T>> => {
        return execute<T>(axios.delete<T>(url, getRequestConfig("DELETE", data, headers)));
    };

    const put = <T>(url: string, data: unknown, headers: Record<string, string> = {}): Promise<AxiosResponse<T>> => {
        return execute<T>(axios.put<T>(url, data, getRequestConfig("PUT", data, headers)));
    };

    const patch = <T>(url: string, data: unknown, headers: Record<string, string> = {}): Promise<AxiosResponse<T>> => {
        return execute<T>(axios.patch<T>(url, data, getRequestConfig("PATCH", data, headers)));
    };

    const execute = <T>(fetcher: Promise<AxiosResponse<unknown>>): Promise<AxiosResponse<T>> => {
        return new Promise((resolve, reject) => {
            fetcher
                .then((resp) => {
                    setTokenFromResponse(resp);
                    resolve(resp as AxiosResponse<T>);
                })
                .catch((err) => {
                    if (err?.response?.status === 401) {
                        sessionStorage.clear();
                        localStorage.clear();
                        removeToken();
                    } else {
                        handleError(err);
                    }

                    reject(err);
                }) as Promise<AxiosResponse<T> | void>;
        });
    };

    const getRequestConfig = (method: Method, data: unknown, headers: Record<string, string>, signal?: AbortSignal) => {
        if (method == "GET") {
            return { ...getHeaders(headers), params: data, signal };
        }
        if (method == "DELETE") {
            return {
                ...getHeaders(headers),
                data,
                signal,
            };
        }
        return { ...getHeaders(headers), signal };
    };

    const getHeaders = (optionalHeaders: Record<string, string>) => {
        const token = getToken();
        return {
            headers: {
                ...(token
                    ? {
                          authorization: token,
                      }
                    : {}),
                ...optionalHeaders,
            },
        };
    };

    const setErrorMessage = (err: AxiosError<{ errorCode?: number }, unknown>) => {
        err.message = err.toString();
    };

    const handleError = (err: AxiosError<{ errorCode?: number }>) => {
        if (err?.response?.status === 420) {
            serviceUnavailable.value = true;
        }
        setErrorMessage(err);
        if (!displayErrors && err.response) {
            return;
        }
    };

    const delay = (t: number, value: unknown = undefined) => {
        return new Promise(function (resolve) {
            setTimeout(resolve.bind(null, value), t);
        });
    };

    type PromiseFunction<T> = () => Promise<T>;

    const retry = <T>(call: PromiseFunction<T>, count = 3): Promise<T> => {
        let lastError: AxiosError;
        return new Promise((resolve, reject) => {
            if (count > 0) {
                call()
                    .then((data: T) => resolve(data))
                    .catch((err) => {
                        lastError = err;
                        delay(500).then(() => retry(call, count - 1));
                    });
            } else {
                reject(lastError);
            }
        });
    };

    return {
        post,
        get,
        del,
        patch,
        put,
        removeToken,
        getToken,
        delay,
        retry,
        serviceUnavailable: readonly(serviceUnavailable),
    };
};

export default useApi;
